import { valueNoise } from '@/play/noise'
import { posKeyXYZ, type Position } from '@/voxelMath'
import type { VoxelData } from '@/voxelTypes'
import { HILLS, type Hill } from './hills'

/**
 * Dig: a sand hill on a beach with a handful of googly-eyed pebble creatures buried in it at
 * different depths. The hills are authored (hills.ts), played in order: a new Dig starts on the
 * first, and "next hill" (events.ts) swaps the world for the next one.
 *
 * Each creature is a rounded pebble of solid colour with a flat top and its eyes on top (two eyes of
 * two cells across, or one big eye of three), so digging down from above finds the eyes first. Eye
 * cells are surrounded by sand on every side but the pebble's: the sand hides them (they are drawn,
 * but inside the sand). Every eye has at least two cells of sand over it.
 */

export const DIG_FOR_EYES_VERSION = 3
export const DIG_BOUNDS: Position = { x: 64, y: 22, z: 64 }

/** The floor under the beach (y = 0): wet sand, solid, never dug. */
export const DIG_FLOOR_Y = 0
const FLOOR_COLOR = 0xcdb68a
const SAND_STRATA = [0xe2cfa4, 0xe9d8b2, 0xf0e2c2, 0xf6ebd2] as const
/** Rocks: grey-brown, darker than any sand, duller than any creature. */
const ROCK_COLOR = 0x8a8178

/** Creature colours: bright, so a corner of one showing in the sand is easy to spot. */
export const DIG_CREATURE_COLORS = [0xff5a5f, 0x3fb8f0, 0x7cc62a, 0xffb81c, 0xa77bff, 0xff7fb0, 0x22c3b0] as const

export const DIG_PALETTE: VoxelData[] = [
  { type: 'sand', color: SAND_STRATA[2] },
  { type: 'solid', color: FLOOR_COLOR },
  ...DIG_CREATURE_COLORS.map((color) => ({ type: 'solid', color }) as VoxelData),
  { type: 'solid', color: ROCK_COLOR },
  { type: 'googly_eye' },
]

/** The rounded profile of a mound at normalised distance d (1 at the edge, 0 beyond it). */
const moundProfile = (d: number) => (d < 1 ? Math.cos(d * Math.PI * 0.5) ** 1.3 : 0)

/** Number of sand cells in column (x, z), above the floor: the top sand cell is at y = height. */
export function sandHeight(x: number, z: number, hill: Hill, bounds: Position): number {
  let lift = 0
  let shape = 0
  for (const m of hill.mounds) {
    const a = m.angle ?? 0
    const dx = x - m.x, dz = z - m.z
    const u = dx * Math.cos(a) + dz * Math.sin(a)
    const v = -dx * Math.sin(a) + dz * Math.cos(a)
    const p = moundProfile(Math.hypot(u / m.rx, v / m.rz))
    lift += p * m.height
    shape = Math.max(shape, p)
  }
  const seed = hill.noiseSeed
  const ripple = (valueNoise(x, z, 9, seed) - 0.5) * 3 + (valueNoise(x, z, 4, seed + 1) - 0.5) * 1.2
  const h = 2 + lift + ripple * hill.ripple * (0.4 + shape)
  return Math.max(1, Math.min(bounds.y - 3, Math.round(h)))
}

export type BuriedCreature = {
  color: number
  /** Centre of the creature's top (x, z), and the y of its eye cells. */
  x: number
  z: number
  eyeY: number
  /** Sand cells over its eyes, at the shallowest eye cell. */
  cover: number
  /** Body cells (eyes not included). */
  body: Position[]
  eyes: Array<{ size: number; cells: Position[] }>
}

/** Pebble radii: a big pebble carries two eyes side by side, a small one a single big eye. */
const PEBBLE_RADIUS: Record<1 | 2, Position> = {
  2: { x: 3.4, y: 2.3, z: 2.9 },
  1: { x: 2.6, y: 2, z: 2.6 },
}

/** The eye squares of a creature centred at (x, z): two small eyes side by side, or one big one. */
function eyeSquares(x: number, z: number, y: number, oneBig: boolean): Array<{ size: number; cells: Position[] }> {
  const square = (x0: number, z0: number, size: number) => {
    const cells: Position[] = []
    for (let i = 0; i < size; i++) for (let j = 0; j < size; j++) cells.push({ x: x0 + i, y, z: z0 + j })
    return { size, cells }
  }
  return oneBig ? [square(x - 1, z - 1, 3)] : [square(x - 2, z - 1, 2), square(x + 1, z - 1, 2)]
}

/**
 * A pebble's body cells relative to its eyes (eyes at y = 0, centred on x = z = 0): a squat
 * ellipsoid cut flat one cell under the eyes, half a radius below its crown so the top layer is
 * wide, and a short pillar under every eye cell so each one rests on the body.
 */
function pebbleBody(eyes: 1 | 2): Position[] {
  const { x: rx, y: ry, z: rz } = PEBBLE_RADIUS[eyes]
  const top = -1
  const cy = top + 0.5 - 0.5 * ry
  const out = new Map<string, Position>()
  for (let x = Math.floor(-rx - 1); x <= Math.ceil(rx + 1); x++) {
    for (let y = Math.floor(cy - ry - 1); y <= top; y++) {
      for (let z = Math.floor(-rz - 1); z <= Math.ceil(rz + 1); z++) {
        const dx = x / rx, dy = (y + 0.5 - cy) / ry, dz = z / rz
        if (dx * dx + dy * dy + dz * dz <= 1) out.set(`${x},${y},${z}`, { x, y, z })
      }
    }
  }
  for (const eye of eyeSquares(0, 0, 0, eyes === 1)) {
    for (const p of eye.cells) for (let y = top; y >= top - 2; y--) out.set(`${p.x},${y},${p.z}`, { x: p.x, y, z: p.z })
  }
  return [...out.values()]
}

/** Sand over the rest of a pebble's body (not its eyes), at the least. */
const BODY_COVER = 2

/** Where a hill's creatures go (no cells yet): cheap, used to recognise a hill from its eyes. */
export function hillCreatures(hill: Hill, bounds: Position = DIG_BOUNDS): BuriedCreature[] {
  return hill.creatures.map((c) => {
    // The highest the eyes can sit: `cover` sand over every eye cell, BODY_COVER over the rest.
    const eyeCols = new Set(eyeSquares(0, 0, 0, c.eyes === 1).flatMap((e) => e.cells.map((p) => `${p.x},${p.z}`)))
    let eyeY = Infinity
    for (const p of pebbleBody(c.eyes)) {
      const eye = eyeCols.has(`${p.x},${p.z}`)
      const y = eye ? 0 : p.y
      const cover = eye ? c.cover : BODY_COVER
      eyeY = Math.min(eyeY, sandHeight(c.x + p.x, c.z + p.z, hill, bounds) - cover - y)
    }
    return {
      color: DIG_CREATURE_COLORS[c.color % DIG_CREATURE_COLORS.length],
      x: c.x,
      z: c.z,
      eyeY,
      cover: c.cover,
      body: pebbleBody(c.eyes).map((p) => ({ x: c.x + p.x, y: eyeY + p.y, z: c.z + p.z })),
      eyes: eyeSquares(c.x, c.z, eyeY, c.eyes === 1),
    }
  })
}

/** Cells of a hill: the floor, the creatures, the rocks and the sand. */
export function hillCells(hill: Hill, bounds: Position = DIG_BOUNDS): { entries: Array<[number, VoxelData]>; creatures: BuriedCreature[] } {
  const heights = new Int32Array(bounds.x * bounds.z)
  for (let x = 0; x < bounds.x; x++) for (let z = 0; z < bounds.z; z++) heights[x * bounds.z + z] = sandHeight(x, z, hill, bounds)
  const heightAt = (x: number, z: number) => heights[x * bounds.z + z]

  const cells = new Map<number, VoxelData>()
  const creatures = hillCreatures(hill, bounds)

  // The pebbles (cut off by the floor, should one ever reach it), with their eyes on top.
  for (const c of creatures) {
    const body: VoxelData = { type: 'solid', color: c.color }
    for (const p of c.body) if (p.y > DIG_FLOOR_Y) cells.set(posKeyXYZ(p.x, p.y, p.z), body)
    const eyeVoxel: VoxelData = { type: 'googly_eye', face: 'y+' }
    for (const eye of c.eyes) for (const p of eye.cells) cells.set(posKeyXYZ(p.x, p.y, p.z), eyeVoxel)
  }

  // Rocks: squat boulders half in the sand (they never overwrite a creature; the tests keep a gap).
  const rock: VoxelData = { type: 'solid', color: ROCK_COLOR }
  for (const r of hill.rocks ?? []) {
    const cy = heightAt(r.x, r.z) + 0.5 - r.sink
    const ry = r.r * 0.7
    for (let x = Math.floor(r.x - r.r); x <= Math.ceil(r.x + r.r); x++) {
      for (let y = Math.max(DIG_FLOOR_Y + 1, Math.floor(cy - ry)); y <= Math.min(bounds.y - 1, Math.ceil(cy + ry)); y++) {
        for (let z = Math.floor(r.z - r.r); z <= Math.ceil(r.z + r.r); z++) {
          const dx = (x - r.x) / r.r, dy = (y + 0.5 - cy) / ry, dz = (z - r.z) / r.r
          const key = posKeyXYZ(x, y, z)
          if (dx * dx + dy * dy + dz * dz <= 1 && !cells.has(key)) cells.set(key, rock)
        }
      }
    }
  }

  // Sand everywhere else up to the surface, in strata; the floor under it all.
  const strata = SAND_STRATA.map((color) => ({ type: 'sand', color }) as VoxelData)
  const floor: VoxelData = { type: 'solid', color: FLOOR_COLOR }
  for (let x = 0; x < bounds.x; x++) {
    for (let z = 0; z < bounds.z; z++) {
      cells.set(posKeyXYZ(x, DIG_FLOOR_Y, z), floor)
      const h = heightAt(x, z)
      for (let y = DIG_FLOOR_Y + 1; y <= h; y++) {
        const key = posKeyXYZ(x, y, z)
        if (cells.has(key)) continue
        const band = Math.min(strata.length - 1, Math.floor((y / 16) * strata.length))
        cells.set(key, strata[band])
      }
    }
  }
  return { entries: Array.from(cells), creatures }
}

const builtHills = new Map<number, { entries: Array<[number, VoxelData]>; sand: number }>()

/** Hill number `index` (0-based, wraps around after the last), built once and kept. */
export function builtHill(index: number): { entries: Array<[number, VoxelData]>; sand: number } {
  const i = hillIndex(index)
  let built = builtHills.get(i)
  if (!built) {
    const { entries } = hillCells(HILLS[i])
    built = { entries, sand: entries.reduce((n, [, v]) => n + (v.type === 'sand' ? 1 : 0), 0) }
    builtHills.set(i, built)
  }
  return built
}

/** A hill number brought into range (after the last hill comes the first again). */
export function hillIndex(index: number): number {
  const n = HILLS.length
  return ((Math.floor(index) % n) + n) % n
}

let eyePrints: string[] | null = null

/** A hill's fingerprint: its eye cells, sorted (creatures never move, so the eyes say which hill it is). */
export function eyePrint(eyeKeys: Iterable<number>): string {
  return [...eyeKeys].sort((a, b) => a - b).join(',')
}

/** Which authored hill a document is, from its eye cells; null for anything else (an old random beach). */
export function whichHill(eyeKeys: Iterable<number>): number | null {
  eyePrints ??= HILLS.map((hill) => eyePrint(hillCreatures(hill).flatMap((c) => c.eyes.flatMap((e) => e.cells.map((p) => posKeyXYZ(p.x, p.y, p.z))))))
  const i = eyePrints.indexOf(eyePrint(eyeKeys))
  return i >= 0 ? i : null
}
